import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTaskByIdQuery, useTaskDetailsUpdateMutation } from 'api';
import { FormInstance, notification } from 'components';
import { ErrorMappingValueType } from 'typings';
import { ITaskDetailsUpdate } from './index';
import dayjs from 'dayjs';

export const useTaskDetailsEdit = (props: { form: FormInstance } & ITaskDetailsUpdate) => {
  const { onCancel, form, open } = props;
  const { id } = useParams() as { id: string };
  const { data: taskDetails = {} } = useTaskByIdQuery({ id });
  const [isSaveBtnDisabled, setIsSaveBtnDisabled] = useState(true);

  const [updateTaskDetails, { isLoading: detailsUpdateIsLoading }] = useTaskDetailsUpdateMutation();
  const [notificationApi, contextHolder] = notification.useNotification();

  const openNotificationWithIcon = useCallback(
    (messageContent: ErrorMappingValueType) => {
      const { type, message } = messageContent;
      notificationApi[type]({
        message: message,
      });
    },
    [notificationApi]
  );

  const onCancelModal = useCallback(() => {
    form.resetFields();
    setIsSaveBtnDisabled(true);
    onCancel();
  }, [form, onCancel]);

  const initialValues = useMemo(() => {
    return {
      assignedTo: taskDetails.assignedTo,
      priorityLevel: taskDetails.priorityLevel,
      entityType: taskDetails.entityType,
      entityValue: taskDetails.entityValue,
      entityId: taskDetails.entityUuid,
      dueDate: dayjs(taskDetails.dueDate),
    };
  }, [taskDetails]);

  useEffect(() => {
    if (open) {
      form.setFieldsValue(initialValues);
    }
  }, [open, form, initialValues]);

  const checkErrors = useCallback(
    async (values: Record<string, any>) => {
      if (values.entityType) {
        form.setFieldValue('entityId', undefined);
      }
    },
    [form]
  );

  const handleError = useCallback(
    (error: any) => {
      if (!error.data.success) {
        const { data = {} } = error.data;
        Object.keys(data).forEach((_key: string) => {
          openNotificationWithIcon({
            type: 'error',
            message: data[_key],
          });
        });
      } else if (error.data.error) {
        openNotificationWithIcon({
          type: 'error',
          message: 'Something went wrong!',
        });
      }
    },
    [openNotificationWithIcon]
  );

  const onFinish = useCallback(async () => {
    try {
      const payload = form.getFieldsValue();
      payload.id = id;
      const { data, error }: any = await updateTaskDetails(payload);
      if (data?.success) {
        openNotificationWithIcon({
          type: 'success',
          message: 'The task details were successfully updated.',
        });
        onCancelModal();
      } else {
        handleError(error);
      }
    } catch (err) {
      console.log(err, 'error');
    }
  }, [updateTaskDetails, openNotificationWithIcon, onCancelModal, id, form, handleError]);

  return {
    onFinish,
    contextHolder,
    checkErrors,
    isSaveBtnDisabled,
    onCancelModal,
    isLoading: detailsUpdateIsLoading,
  };
};
